import {
  Badge,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@devhop/ui";
import { Link, useLocation } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import type { PermissionId } from "@/modules/roles/domain/contracts/permissions";
import type {
  NavCollapsible,
  NavGroup as NavGroupProps,
  NavItem,
  NavLink,
} from "./types";

function filterNavTree(
  nodes: NavItem[],
  hasAll: (perms: PermissionId[]) => boolean,
): NavItem[] {
  const out: NavItem[] = [];
  for (const item of nodes) {
    if (!hasAll(item.requiredPermissions ?? [])) continue;
    if ("items" in item && item.items) {
      const children = filterNavTree(item.items, hasAll);
      if (children.length > 0) out.push({ ...item, items: children });
    } else if ("url" in item && item.url) {
      out.push(item);
    }
  }
  return out;
}

function navItemKey(item: NavItem): string {
  if ("url" in item && item.url) return `${item.title}:${String(item.url)}`;
  return `${item.title}:collapsible`;
}

export function NavGroup({ title, items }: NavGroupProps) {
  const { state, isMobile } = useSidebar();
  const href = useLocation({ select: (location) => location.href });
  const { hasAll } = usePermissions();
  const visibleItems = filterNavTree(items, hasAll);
  if (visibleItems.length === 0) return null;
  return (
    <SidebarGroup>
      <SidebarGroupLabel>{title}</SidebarGroupLabel>
      <SidebarMenu>
        {visibleItems.map((item) => {
          const key = navItemKey(item);

          if (!("items" in item) || !item.items)
            return <SidebarMenuLink key={key} item={item} href={href} />;

          if (state === "collapsed" && !isMobile)
            return (
              <SidebarMenuCollapsedDropdown key={key} item={item} href={href} />
            );

          return <SidebarMenuCollapsible key={key} item={item} href={href} />;
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
}

function NavBadge({
  children,
  tone = "default",
}: {
  children: ReactNode;
  tone?: "default" | "warning" | "danger";
}) {
  const toneClass =
    tone === "danger"
      ? "border-transparent bg-red-600 text-white hover:bg-red-600"
      : tone === "warning"
        ? "border-transparent bg-amber-500 text-white hover:bg-amber-500"
        : "";
  return (
    <Badge className={`rounded-full px-1 py-0 text-xs ${toneClass}`.trim()}>
      {children}
    </Badge>
  );
}

function SidebarMenuLink({ item, href }: { item: NavLink; href: string }) {
  const { setOpenMobile } = useSidebar();
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={checkIsActive(href, item)}
        tooltip={item.title}
      >
        <Link to={item.url} onClick={() => setOpenMobile(false)}>
          {item.icon && <item.icon />}
          <span>{item.title}</span>
          {item.badge && (
            <NavBadge tone={item.badgeTone}>{item.badge}</NavBadge>
          )}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function SidebarMenuCollapsible({
  item,
  href,
}: {
  item: NavCollapsible;
  href: string;
}) {
  return (
    <Collapsible
      asChild
      defaultOpen={checkBranchActive(href, item)}
      className="group/collapsible"
    >
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton tooltip={item.title}>
            {item.icon && <item.icon />}
            <span>{item.title}</span>
            {item.badge && (
            <NavBadge tone={item.badgeTone}>{item.badge}</NavBadge>
          )}
            <ChevronRight className="ms-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent className="CollapsibleContent">
          <SidebarMenuSub>
            {item.items.map((subItem) => (
              <SidebarSubNav
                key={navItemKey(subItem)}
                item={subItem}
                href={href}
              />
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

function SidebarSubNav({ item, href }: { item: NavItem; href: string }) {
  const { setOpenMobile } = useSidebar();

  if (!("items" in item) || !item.items) {
    return (
      <SidebarMenuSubItem>
        <SidebarMenuSubButton asChild isActive={checkIsActive(href, item)}>
          <Link to={item.url} onClick={() => setOpenMobile(false)}>
            {item.icon && <item.icon />}
            <span>{item.title}</span>
            {item.badge && (
            <NavBadge tone={item.badgeTone}>{item.badge}</NavBadge>
          )}
          </Link>
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
    );
  }

  return (
    <SidebarMenuSubItem>
      <Collapsible
        asChild
        defaultOpen={checkBranchActive(href, item)}
        className="group/subcollapsible w-full min-w-0"
      >
        <div>
          <CollapsibleTrigger asChild>
            <SidebarMenuSubButton className="w-full">
              {item.icon && <item.icon />}
              <span>{item.title}</span>
              {item.badge && (
            <NavBadge tone={item.badgeTone}>{item.badge}</NavBadge>
          )}
              <ChevronRight className="ms-auto size-4 shrink-0 transition-transform duration-200 group-data-[state=open]/subcollapsible:rotate-90" />
            </SidebarMenuSubButton>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SidebarMenuSub className="mx-2 border-sidebar-border border-l pl-2">
              {item.items.map((nested) => (
                <SidebarSubNav
                  key={navItemKey(nested)}
                  item={nested}
                  href={href}
                />
              ))}
            </SidebarMenuSub>
          </CollapsibleContent>
        </div>
      </Collapsible>
    </SidebarMenuSubItem>
  );
}

function flattenNavForDropdown(
  nodes: NavItem[],
  prefix: string[] = [],
): {
  url: NavLink["url"];
  title: string;
  icon?: NavLink["icon"];
  badge?: string;
}[] {
  const rows: {
    url: NavLink["url"];
    title: string;
    icon?: NavLink["icon"];
    badge?: string;
  }[] = [];
  for (const node of nodes) {
    if ("url" in node && node.url) {
      const label = [...prefix, node.title].join(" › ");
      rows.push({
        url: node.url,
        title: label,
        icon: node.icon,
        badge: node.badge,
      });
    } else if ("items" in node && node.items) {
      rows.push(...flattenNavForDropdown(node.items, [...prefix, node.title]));
    }
  }
  return rows;
}

function SidebarMenuCollapsedDropdown({
  item,
  href,
}: {
  item: NavCollapsible;
  href: string;
}) {
  const flat = flattenNavForDropdown(item.items);
  return (
    <SidebarMenuItem>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton
            tooltip={item.title}
            isActive={checkBranchActive(href, item)}
          >
            {item.icon && <item.icon />}
            <span>{item.title}</span>
            {item.badge && (
            <NavBadge tone={item.badgeTone}>{item.badge}</NavBadge>
          )}
            <ChevronRight className="ms-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="right" align="start" sideOffset={4}>
          <DropdownMenuLabel>
            {item.title} {item.badge ? `(${item.badge})` : ""}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {flat.map((row) => (
            <DropdownMenuItem key={`${row.title}-${String(row.url)}`} asChild>
              <Link
                to={row.url}
                className={`${
                  href === String(row.url) ||
                  href.split("?")[0] === String(row.url)
                    ? "bg-secondary"
                    : ""
                }`}
              >
                {row.icon && <row.icon />}
                <span className="max-w-52 text-wrap">{row.title}</span>
                {row.badge && (
                  <span className="ms-auto text-xs">{row.badge}</span>
                )}
              </Link>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
}

/** ลิงก์ตรงกับ URL ปัจจุบัน */
function checkIsActive(href: string, item: NavItem) {
  if (!("url" in item) || !item.url) return false;
  const u = String(item.url);
  return href === u || href.split("?")[0] === u;
}

/** โฟลเดอร์เปิดเมื่อมีลูกที่ active (ซ้อนได้หลายชั้น) */
function checkBranchActive(href: string, item: NavItem): boolean {
  if ("url" in item && item.url && checkIsActive(href, item)) return true;
  if ("items" in item && item.items) {
    return item.items.some((child) => checkBranchActive(href, child));
  }
  return false;
}
